import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Text, Stack, Group, Badge, Loader, Center, Button, SegmentedControl, ThemeIcon } from '@mantine/core'
import {
  IconBell, IconArrowsExchange, IconShieldExclamation, IconPackage,
  IconTool,
} from '@tabler/icons-react'
import { notifications as toast } from '@mantine/notifications'
import {
  getNotifications, markNotificationRead, markAllNotificationsRead,
  getPendingBorrows, approveBorrow, rejectBorrow,
  getTransfers, approveTransfer,
} from '../api/client'
import { useBreakpoint } from '../hooks/useBreakpoint'

type FilterTab = 'all' | 'unread' | 'action'

// ไอคอน/สี ต่อ "ประเภท" การแจ้งเตือน — ตรงกับ NotificationType enum จริงใน backend
const TYPE_META: Record<string, { icon: any; color: string }> = {
  WARRANTY_EXPIRING: { icon: IconShieldExclamation, color: '#f59e0b' },
  OVERDUE_BORROW:    { icon: IconArrowsExchange, color: '#dc2626' },
  MAINTENANCE_DUE:   { icon: IconTool, color: '#2563eb' },
  ASSET_LOST:        { icon: IconShieldExclamation, color: '#dc2626' },
  ASSET_DAMAGED:     { icon: IconTool, color: '#dc2626' },
  ASSET_FOUND:       { icon: IconPackage, color: '#059669' },
}

export default function NotificationsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isSmall } = useBreakpoint()
  const [tab, setTab] = useState<FilterTab>('all')
  const [loading, setLoading] = useState(true)
  const [notifs, setNotifs] = useState<any[]>([])
  const [pendingBorrows, setPendingBorrows] = useState<any[]>([])
  const [pendingTransfers, setPendingTransfers] = useState<any[]>([])
  const [actingId, setActingId] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const [n, b, tr] = await Promise.all([
        getNotifications().then(r => r.data ?? []).catch(() => []),
        getPendingBorrows().then(r => (r.data ?? []).filter((x: any) => x.status === 'PENDING')).catch(() => []),
        getTransfers().then(r => (r.data ?? []).filter((x: any) => x.status === 'PENDING')).catch(() => []),
      ])
      setNotifs(n)
      setPendingBorrows(b)
      setPendingTransfers(tr)
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  // ── รีเฟรชอัตโนมัติทุกครั้งที่กลับมาที่หน้านี้ (สลับแอปแล้วกลับมา, หรือ navigate กลับมาที่ tab นี้) ──
  // ป้องกันปัญหารายการค้าง (someone else มาจัดการไปแล้วแต่หน้านี้ยังไม่รู้) ตั้งแต่ต้นทาง
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [])

  const handleMarkRead = async (id: number) => {
    await markNotificationRead(id).catch(() => {})
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n))
  }

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead().catch(() => {})
    setNotifs(prev => prev.map(n => ({ ...n, isRead: true })))
  }

  const handleApproveBorrow = async (id: number) => {
    setActingId(`b${id}`)
    try {
      await approveBorrow(id)
      toast.show({ color: 'teal', message: t('notifications.approved', 'Approved') })
      setPendingBorrows(prev => prev.filter(b => b.id !== id))
    } catch (e: any) {
      const msg: string = e.response?.data?.message ?? ''
      if (msg.toLowerCase().includes('only pending')) {
        // ← รายการนี้ถูกคนอื่น (หรือเครื่องอื่น) จัดการไปแล้วตั้งแต่ก่อนหน้า แค่หน้านี้ยังไม่รีเฟรช
        // ไม่ใช่ปัญหาสิทธิ์ — เอารายการเก่าออกจาก list ทันที + บอกให้ชัดว่าเกิดอะไรขึ้นจริงๆ
        toast.show({ color: 'orange', message: t('notifications.alreadyHandled', 'This request was already handled — list refreshed') })
        setPendingBorrows(prev => prev.filter(b => b.id !== id))
        load()
      } else {
        toast.show({ color: 'red', message: msg || t('common.error') })
      }
    } finally { setActingId(null) }
  }

  const handleRejectBorrow = async (id: number) => {
    setActingId(`b${id}`)
    try {
      await rejectBorrow(id)
      toast.show({ color: 'gray', message: t('notifications.rejected', 'Rejected') })
      setPendingBorrows(prev => prev.filter(b => b.id !== id))
    } catch (e: any) {
      const msg: string = e.response?.data?.message ?? ''
      if (msg.toLowerCase().includes('only pending')) {
        toast.show({ color: 'orange', message: t('notifications.alreadyHandled', 'This request was already handled — list refreshed') })
        setPendingBorrows(prev => prev.filter(b => b.id !== id))
        load()
      } else {
        toast.show({ color: 'red', message: msg || t('common.error') })
      }
    } finally { setActingId(null) }
  }

  const handleApproveTransfer = async (id: number) => {
    setActingId(`t${id}`)
    try {
      await approveTransfer(id)
      toast.show({ color: 'teal', message: t('notifications.approved', 'Approved') })
      setPendingTransfers(prev => prev.filter(x => x.id !== id))
    } catch (e: any) {
      const msg: string = e.response?.data?.message ?? ''
      if (msg.toLowerCase().includes('already')) {
        toast.show({ color: 'orange', message: t('notifications.alreadyHandled', 'This request was already handled — list refreshed') })
        setPendingTransfers(prev => prev.filter(x => x.id !== id))
        load()
      } else {
        toast.show({ color: 'red', message: msg || t('common.error') })
      }
    } finally { setActingId(null) }
  }

  const unreadCount = notifs.filter(n => !n.isRead).length
  const actionCount = pendingBorrows.length + pendingTransfers.length

  // รวม action-required items (borrow/transfer) เป็นรูปแบบเดียวกับ notification ทั่วไป เพื่อโชว์ในลิสต์เดียวกัน
  const actionItems = [
    ...pendingBorrows.map(b => ({
      kind: 'borrow', id: b.id, title: t('notifications.borrowRequestTitle', 'Borrow Request'),
      message: `${b.borrowerName ?? ''} — ${(b.assets ?? []).map((a: any) => a.name).join(', ')}`,
      createdAt: b.createdAt, isRead: false,
    })),
    ...pendingTransfers.map(tr => ({
      kind: 'transfer', id: tr.id, title: t('notifications.transferRequestTitle', 'Transfer Request'),
      message: `${(tr.items ?? []).map((i: any) => i.name).join(', ')} → ${tr.toDept ?? ''}`,
      createdAt: tr.createdAt, isRead: false,
    })),
  ]

  let displayList: any[] = []
  if (tab === 'action') {
    displayList = actionItems
  } else if (tab === 'unread') {
    displayList = [...actionItems, ...notifs.filter(n => !n.isRead)]
  } else {
    displayList = [...actionItems, ...notifs]
  }

  return (
    <Box p={isSmall ? 16 : 32}>
      <Group justify="space-between" mb="md">
        <Group gap={8}>
          <IconBell size={20} color="#0d9488" />
          <Text fw={600} size={isSmall ? 'lg' : 'xl'}>{t('notifications.title', 'Notifications')}</Text>
        </Group>
        {unreadCount > 0 && (
          <Button variant="subtle" size="xs" onClick={handleMarkAllRead}>
            {t('notifications.markAllRead', 'Mark all as read')}
          </Button>
        )}
      </Group>

      <SegmentedControl
        fullWidth mb="md"
        value={tab}
        onChange={(v) => setTab(v as FilterTab)}
        data={[
          { label: t('notifications.tabAll', 'All'), value: 'all' },
          { label: `${t('notifications.tabUnread', 'Unread')}${unreadCount ? ` (${unreadCount})` : ''}`, value: 'unread' },
          { label: `${t('notifications.tabAction', 'Action Required')}${actionCount ? ` (${actionCount})` : ''}`, value: 'action' },
        ]}
      />

      {loading ? (
        <Center py="xl"><Loader color="teal" /></Center>
      ) : displayList.length === 0 ? (
        <Center py="xl"><Text size="sm" c="dimmed">{t('notifications.empty', 'No notifications')}</Text></Center>
      ) : (
        <Stack gap="xs">
          {displayList.map((item: any) => {
            if (item.kind === 'borrow' || item.kind === 'transfer') {
              const acting = actingId === `${item.kind[0]}${item.id}`
              return (
                <Box key={`${item.kind}-${item.id}`} style={{ background: '#eff6ff', borderRadius: 14, border: '1px solid #dbeafe', padding: 14 }}>
                  <Group gap={10} align="flex-start" wrap="nowrap">
                    <ThemeIcon size={36} radius="xl" color="blue" variant="light"><IconArrowsExchange size={18} /></ThemeIcon>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" fw={600}>{item.title}</Text>
                      <Text size="xs" c="dimmed" truncate>{item.message}</Text>
                      <Group gap="xs" mt={8}>
                        <Button size="xs" color="teal" loading={acting}
                          onClick={() => item.kind === 'borrow' ? handleApproveBorrow(item.id) : handleApproveTransfer(item.id)}>
                          {t('notifications.approve', 'Approve')}
                        </Button>
                        {item.kind === 'borrow' && (
                          <Button size="xs" color="red" variant="light" loading={acting}
                            onClick={() => handleRejectBorrow(item.id)}>
                            {t('notifications.reject', 'Reject')}
                          </Button>
                        )}
                      </Group>
                    </Box>
                  </Group>
                </Box>
              )
            }

            const meta = TYPE_META[item.type] ?? { icon: IconBell, color: '#64748b' }
            return (
              <Box key={item.id}
                onClick={() => !item.isRead && handleMarkRead(item.id)}
                style={{
                  background: item.isRead ? '#fff' : '#f0fdfa',
                  borderRadius: 14, border: '1px solid #ebebeb', padding: 14,
                  cursor: item.isRead ? 'default' : 'pointer',
                }}>
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Group gap={10} align="flex-start" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                    <ThemeIcon size={36} radius="xl" variant="light" style={{ background: `${meta.color}1a`, color: meta.color }}>
                      <meta.icon size={18} />
                    </ThemeIcon>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" fw={item.isRead ? 400 : 600}>{item.title ?? item.message}</Text>
                      {item.title && item.message && <Text size="xs" c="dimmed" mt={2}>{item.message}</Text>}
                    </Box>
                  </Group>
                  {!item.isRead && <Badge size="xs" color="teal" circle />}
                </Group>
              </Box>
            )
          })}
        </Stack>
      )}
    </Box>
  )
}

